import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, FileAudio, Pause, Play, RefreshCw } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { MessageModal } from "../../components/ui/MessageModal";
import { Modal } from "../../components/ui/Modal";
import { WaveformPlayer } from "../../components/WaveformPlayer";
import { ScrollShadow } from "../../components/ui/ScrollShadow";
import { AgentStatusChip } from "../../components/AgentStatusChip";
import { ErrorTile, Td, Th, fmtDateTime, fmtDuration } from "../../components/reports/ReportBits";
import { useLivePolling } from "../../hooks/useLivePolling";
import { useSession } from "../../hooks/useSession";
import { useOpenAsCustomer } from "../../hooks/useOpenAsCustomer";
import { HttpError } from "../../api/http";
import { formatPhoneNumber } from "../../lib/phone";
import { pbxExtensionsApi, type Paged, type PbxAgentLog, type PbxCall, type PbxEvent, type PbxExtensionDetail, type PbxLogType } from "../../api/pbxExtensionsApi";
import { ExtensionBadge, LogTypePill, OnCallChip, Pager, Pill, RegistrationChip, SegmentedFilter, fmtAgo, identityLabel, inputStyle, logDetail, monoStyle, phoneModel, secondsSince, wallClockInTz } from "./pbxExtensionBits";

type Tab = "events" | "log" | "calls";
const POLL_MS = 5000;

/**
 * IPPBX → Extensions → one extension: live header card plus the history
 * (SIP events, agent log, calls) and the two actions.
 */
export function PbxExtensionDetailPage() {
  const { agentId: agentIdParam } = useParams<{ agentId: string }>();
  const agentId = Number(agentIdParam);
  const { session } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) || "events";
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    next.set("tab", t);
    setParams(next, { replace: true });
  };

  const [paused, setPaused] = useState(false);
  const { open: openAsCustomer, switching, error: switchError } = useOpenAsCustomer();

  const [notFound, setNotFound] = useState<"notfound" | "forbidden" | null>(null);
  const { data, loading, error, refresh } = useLivePolling<PbxExtensionDetail>({
    fetcher: async (signal) => {
      try {
        const d = await pbxExtensionsApi.get(agentId, signal);
        setNotFound(null);
        return d;
      } catch (e) {
        if (e instanceof HttpError && (e.status === 404 || e.status === 403)) setNotFound(e.status === 404 ? "notfound" : "forbidden");
        throw e;
      }
    },
    intervalMs: POLL_MS,
    enabled: !!session?.isAuthenticated && Number.isFinite(agentId) && !paused && switching == null && notFound == null,
    deps: [agentId],
  });

  const ext = data?.extension ?? null;
  const tz = data?.customerTimeZoneIana || "UTC";

  // ---- actions ------------------------------------------------------------------------
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ title: string; body: string } | null>(null);

  const doReregister = async () => {
    if (!ext) return;
    setBusy(true);
    try {
      const r = await pbxExtensionsApi.reregister(ext.agentId);
      setMessage({
        title: r.alreadyPending ? "Re-registration already pending" : "Re-registration requested",
        body: `${ext.agentName} (${r.pbxUsername}) will be flushed from the PBX within a few seconds; the phone registers again at its next interval, usually within 1–3 minutes.`,
      });
      void refresh();
    } catch (e) {
      setMessage({ title: "Re-registration failed", body: e instanceof Error ? e.message : "Unknown error" });
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  const stateReason = useMemo(() => {
    if (!ext) return "";
    switch (ext.registrationState) {
      case "registered":
        return `Last register ${fmtAgo(ext.secondsSinceRegister)} from ${ext.ipAddress ?? "?"} on ${ext.pbxHostname ?? ext.lastRegisterHostname ?? "the PBX"}; timeout ${ext.pbxTimeoutSeconds ?? 180} s.`;
      case "unregistered":
        return `The phone unregistered ${fmtAgo(secondsSince(ext.lastEventUtc))} (${fmtDateTime(ext.lastEventUtc, tz)}).`;
      case "expired":
        return `No register seen for ${fmtAgo(ext.secondsSinceRegister)} (timeout ${ext.pbxTimeoutSeconds ?? 180} s); the phone may be offline or unable to reach the PBX.`;
      default:
        return ext.pbxUsername ? "No registration has ever been seen for this login; check the phone's SIP credentials." : "This destination has no PBX username yet.";
    }
  }, [ext, tz]);

  const title = ext ? ext.agentName : notFound ? "Extension" : "Loading…";

  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <Link to="/ippbx/extensions" className="btn btn-secondary" style={{ padding: "4px 8px" }} aria-label="Back to extensions"><ArrowLeft size={16} /></Link>
            {title}
          </span>
        }
        actions={
          ext && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <button type="button" className="btn btn-secondary" onClick={() => setPaused((p) => !p)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {paused ? <Play size={14} /> : <Pause size={14} />} {paused ? "Resume" : "Pause"}
              </button>
              <button type="button" className="btn btn-secondary" disabled={!ext.pbxUsername} onClick={() => setConfirm(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <RefreshCw size={14} /> Force re-register
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!ext.customerEnabled || switching != null}
                title={ext.customerEnabled ? "Open the destination as this customer" : "This customer is disabled and cannot be impersonated."}
                onClick={() => openAsCustomer(ext.customerId, `/destinations/${ext.agentId}`)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <ExternalLink size={14} /> Open destination
              </button>
            </div>
          )
        }
      />
      <div className="dashboard" style={{ display: "grid", gap: 16 }}>
        {switchError && <div className="alert alert-error">{switchError}</div>}
        {notFound === "notfound" && (
          <div className="tile">
            <h3 style={{ margin: 0 }}>Extension not found</h3>
            <p style={{ margin: 0, color: "var(--portal-muted)" }}>This agent does not exist or has no PBX login. <Link to="/ippbx/extensions">Back to the list</Link>.</p>
          </div>
        )}
        {notFound === "forbidden" && (
          <div className="tile">
            <h3 style={{ margin: 0 }}>Not your customer</h3>
            <p style={{ margin: 0, color: "var(--portal-muted)" }}>This extension belongs to a customer of another manager. <Link to="/ippbx/extensions">Back to the list</Link>.</p>
          </div>
        )}
        {error != null && notFound == null && <ErrorTile message="The extension could not be refreshed. Showing the last known data." />}
        {loading && !data && notFound == null && <div style={{ color: "var(--portal-muted)" }}>Loading extension…</div>}

        {ext && (
          <>
            <section className="tile" style={{ gap: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {ext.activeCalls > 0 ? <OnCallChip count={ext.activeCalls} /> : <RegistrationChip state={ext.registrationState} />}
                <AgentStatusChip statusId={ext.statusId} statusName={ext.statusName} size="sm" />
                {!ext.phoneEnabled && <Pill tone="muted">phone off</Pill>}
                {ext.unregisters24h >= 3 && <Pill tone="warn">{ext.unregisters24h} flaps in 24 h</Pill>}
                {data?.internalCallsEnabled ? <Pill>Internal calls on</Pill> : <Pill tone="muted">Internal calls off</Pill>}
                <span style={{ marginLeft: "auto", fontSize: "0.8rem", color: "var(--portal-muted)" }}>Times in {tz}</span>
              </div>
              <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>{stateReason}</p>
              <dl className="pbx-kv" style={{ margin: 0 }}>
                <div><dt>Customer</dt><dd>{ext.customerName}{ext.customerEnabled ? "" : " (disabled)"}<div style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>{ext.managerName}</div></dd></div>
                <div><dt>Extension</dt><dd><ExtensionBadge extension={ext.pbxExtension} /> <span style={{ color: "var(--portal-muted)", fontSize: "0.85rem" }}>ref {ext.agentRef}</span><div style={monoStyle}>{ext.pbxUsername ?? "—"}</div>{ext.pbxUsername && <Link to={`/ippbx/banlist?tab=logs&tool=userlog&target=${encodeURIComponent(ext.pbxUsername)}`} style={{ fontSize: "0.8rem" }}>Failed logins (userlog)</Link>}</dd></div>
                <div><dt>PBX host</dt><dd>{ext.pbxHostname ?? ext.lastRegisterHostname ?? "—"}</dd></div>
                <div><dt>IP address</dt><dd><span style={monoStyle}>{ext.ipAddress ?? "—"}</span>{ext.ipAddress && <div><Link to={`/ippbx/banlist?tab=lookup&ip=${encodeURIComponent(ext.ipAddress)}`} style={{ fontSize: "0.8rem" }}>Jailed? (banlist)</Link></div>}</dd></div>
                <div><dt>Phone</dt><dd>{phoneModel(ext.userAgent)}<div style={{ fontSize: "0.78rem", color: "var(--portal-muted)", wordBreak: "break-all" }}>{ext.userAgent ?? ""}</div></dd></div>
                <div><dt>Last register</dt><dd>{ext.lastRegisterUtc ? `${fmtDateTime(ext.lastRegisterUtc, tz)} (${fmtAgo(ext.secondsSinceRegister)})` : "never"}</dd></div>
                <div><dt>Last event</dt><dd>{ext.lastEventUtc ? `${ext.lastEventType?.replace("sofia::", "") ?? ""} · ${fmtDateTime(ext.lastEventUtc, tz)}` : "—"}</dd></div>
                <div><dt>Outbound identity</dt><dd>{ext.pbxIdentityMode === "denied" ? "Denied (no outbound calls)" : identityLabel(ext.pbxIdentityMode, ext.pbxIdentityNumber)}</dd></div>
                <div><dt>Calls today (UTC)</dt><dd>{ext.callsTodayIn} in · {ext.callsTodayOut ?? "n/a"} out<div style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>talk time {ext.talkSecondsToday > 0 ? fmtDuration(ext.talkSecondsToday) : "—"}</div></dd></div>
                <div><dt>Last call</dt><dd>{ext.lastCallUtc ? fmtDateTime(ext.lastCallUtc, tz) : "—"}</dd></div>
                <div><dt>Settings</dt><dd>max channels {ext.maxChannels} · {ext.recordCalls ? "records calls" : "no recording"} · timeout {ext.pbxTimeoutSeconds ?? 180} s</dd></div>
              </dl>
            </section>

            <section className="tile" style={{ gap: 12 }}>
              <div className="pbx-tabs" role="tablist">
                {(["events", "log", "calls"] as Tab[]).map((t) => (
                  <button key={t} type="button" role="tab" aria-selected={tab === t} className="pbx-tab" onClick={() => setTab(t)}>
                    {t === "events" ? "Registration events" : t === "log" ? "Agent log" : "Calls"}
                  </button>
                ))}
              </div>
              {tab === "events" && <EventsTab agentId={ext.agentId} tz={tz} paused={paused} hasUsername={!!ext.pbxUsername} />}
              {tab === "log" && <LogTab agentId={ext.agentId} tz={tz} />}
              {tab === "calls" && <CallsTab agentId={ext.agentId} tz={tz} outboundTracking={data?.outboundTrackingAvailable ?? false} identityMode={ext.pbxIdentityMode} identityNumber={ext.pbxIdentityNumber} />}
            </section>
          </>
        )}
      </div>

      <ConfirmModal open={confirm} onClose={() => (busy ? undefined : setConfirm(false))} title="Force re-register" confirmLabel="Re-register" tone="caution" busy={busy} onConfirm={doReregister}>
        {ext && (
          <p style={{ margin: 0 }}>
            Flush the registration of <strong>{ext.agentName}</strong> ({ext.pbxUsername}){ext.pbxHostname ? ` on ${ext.pbxHostname}` : ""}? The phone registers again at its next interval, usually within 1–3 minutes. Active calls are not affected.
          </p>
        )}
      </ConfirmModal>
      <MessageModal open={message != null} onClose={() => setMessage(null)} title={message?.title ?? ""}>
        <p style={{ margin: 0 }}>{message?.body}</p>
      </MessageModal>
    </>
  );
}

// ---- Events ---------------------------------------------------------------------------

function EventsTab({ agentId, tz, paused, hasUsername }: { agentId: number; tz: string; paused: boolean; hasUsername: boolean }) {
  const [eventType, setEventType] = useState<"all" | "register" | "unregister">("all");
  const [page, setPage] = useState(1);
  const pageSize = 100;

  const { data, loading, error } = useLivePolling<Paged<PbxEvent>>({
    fetcher: (signal) => pbxExtensionsApi.events(agentId, { eventType: eventType === "all" ? null : eventType, page, pageSize }, signal),
    // Only the first page follows the live feed; older pages are static history.
    intervalMs: page === 1 ? POLL_MS : 10 * 60 * 1000,
    enabled: hasUsername && !(paused && page === 1),
    deps: [agentId, eventType, page],
  });

  useEffect(() => { setPage(1); }, [eventType]);

  if (!hasUsername) return <div style={{ color: "var(--portal-muted)" }}>No PBX username, so there are no registration events.</div>;

  return (
    <div className="pbx-tab-panel" style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <SegmentedFilter value={eventType} options={[{ value: "all", label: "All" }, { value: "register", label: "Register" }, { value: "unregister", label: "Unregister" }]} onChange={setEventType} ariaLabel="Event type" />
        <span style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>The PBX keeps events for a few days; the first page updates live{paused ? " (paused)" : ""}.</span>
      </div>
      {error != null && <ErrorTile message="Events could not be loaded." />}
      {loading && !data ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading events…</div>
      ) : (data?.items.length ?? 0) === 0 ? (
        <div style={{ color: "var(--portal-muted)" }}>No events in the retention window.</div>
      ) : (
        <ScrollShadow>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead><tr><Th>Time</Th><Th>Event</Th><Th>IP</Th><Th>Phone</Th></tr></thead>
            <tbody>
              {data!.items.map((e) => (
                <tr key={e.pbxEventId}>
                  <Td>{fmtDateTime(e.timestampUtc, tz)}</Td>
                  <Td>{e.eventType === "sofia::register" ? <RegistrationChip state="registered" size="sm" /> : <RegistrationChip state="unregistered" size="sm" />}</Td>
                  <Td><span style={monoStyle}>{e.ipAddress ?? "—"}</span></Td>
                  <Td><span title={e.userAgent ?? undefined}>{phoneModel(e.userAgent)}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
      )}
      {data && <Pager page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
    </div>
  );
}

// ---- Agent log ------------------------------------------------------------------------

/** Last 7 days up to the end of today (customer zone), so new events keep showing up without editing the range. */
function useDefaultRange(tz: string) {
  return useMemo(() => {
    const now = new Date();
    const from = new Date(now.getTime() - 7 * 86400 * 1000);
    return { from: `${wallClockInTz(from, tz).slice(0, 10)}T00:00`, to: `${wallClockInTz(now, tz).slice(0, 10)}T23:59` };
  }, [tz]);
}

function RangeBar({ from, to, tz, onChange, onGo, busy, children }: { from: string; to: string; tz: string; onChange: (f: string, t: string) => void; onGo: () => void; busy: boolean; children?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
      <label style={{ display: "grid", gap: 2, fontSize: "0.75rem", color: "var(--portal-muted)" }}>
        From ({tz})
        <input type="datetime-local" value={from} onChange={(e) => onChange(e.target.value, to)} style={inputStyle} />
      </label>
      <label style={{ display: "grid", gap: 2, fontSize: "0.75rem", color: "var(--portal-muted)" }}>
        To ({tz})
        <input type="datetime-local" value={to} onChange={(e) => onChange(from, e.target.value)} style={inputStyle} />
      </label>
      {children}
      <button type="button" className="btn btn-primary" onClick={onGo} disabled={busy}>{busy ? "Loading…" : "Show"}</button>
    </div>
  );
}

function LogTab({ agentId, tz }: { agentId: number; tz: string }) {
  const def = useDefaultRange(tz);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [logTypeId, setLogTypeId] = useState<number | null>(null);
  const [types, setTypes] = useState<PbxLogType[]>([]);
  const [data, setData] = useState<Paged<PbxAgentLog> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pageSize = 100;

  useEffect(() => {
    pbxExtensionsApi.logTypes().then(setTypes).catch(() => undefined);
  }, []);

  const load = useCallback(async (p: number) => {
    setBusy(true);
    setErr(null);
    try {
      setData(await pbxExtensionsApi.log(agentId, { from, to, logTypeId, page: p, pageSize }));
    } catch (e) {
      setErr(e instanceof HttpError && e.status === 400 ? "Check the date range (at most 92 days, from before to)." : "The log could not be loaded.");
    } finally {
      setBusy(false);
    }
  }, [agentId, from, to, logTypeId]);

  useEffect(() => { void load(1); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="pbx-tab-panel" style={{ display: "grid", gap: 12 }}>
      <RangeBar from={from} to={to} tz={tz} onChange={(f, t) => { setFrom(f); setTo(t); }} onGo={() => load(1)} busy={busy}>
        <label style={{ display: "grid", gap: 2, fontSize: "0.75rem", color: "var(--portal-muted)" }}>
          Type
          <select value={logTypeId ?? ""} onChange={(e) => setLogTypeId(e.target.value ? Number(e.target.value) : null)} style={inputStyle}>
            <option value="">All types</option>
            {types.map((t) => <option key={t.logTypeId} value={t.logTypeId}>{t.name}</option>)}
          </select>
        </label>
      </RangeBar>
      {err && <ErrorTile message={err} />}
      {data && data.items.length === 0 && <div style={{ color: "var(--portal-muted)" }}>No log entries in this range.</div>}
      {data && data.items.length > 0 && (
        <ScrollShadow>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead><tr><Th>Time</Th><Th>Type</Th><Th>Detail</Th></tr></thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.statsAgentLogId}>
                  <Td>{fmtDateTime(l.logDatetimeUtc, tz)}</Td>
                  <Td><LogTypePill logTypeId={l.logTypeId} logTypeName={l.logTypeName} title={l.logTypeDescription || l.logTypeName} /></Td>
                  <Td><span style={{ whiteSpace: "normal" }}>{logDetail(l.logTypeId, l.logTypeName, l.infoString, l.infoInt)}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
      )}
      {data && <Pager page={data.page} pageSize={data.pageSize} total={data.total} onPage={(p) => load(p)} busy={busy} />}
    </div>
  );
}

// ---- Calls ----------------------------------------------------------------------------

function CallsTab({ agentId, tz, outboundTracking, identityMode, identityNumber }: { agentId: number; tz: string; outboundTracking: boolean; identityMode: string; identityNumber: string | null }) {
  // What the extension shows as "called number" on calls to it: its outbound identity when it has one.
  const extensionLabel = identityMode === "number" && identityNumber ? formatPhoneNumber(identityNumber) : "extension";
  const def = useDefaultRange(tz);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [data, setData] = useState<Paged<PbxCall> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [playing, setPlaying] = useState<PbxCall | null>(null);
  const pageSize = 50;

  const load = useCallback(async (p: number) => {
    setBusy(true);
    setErr(null);
    try {
      setData(await pbxExtensionsApi.calls(agentId, { from, to, page: p, pageSize }));
    } catch (e) {
      setErr(e instanceof HttpError && e.status === 400 ? "Check the date range (at most 92 days, from before to)." : "The calls could not be loaded.");
    } finally {
      setBusy(false);
    }
  }, [agentId, from, to]);

  useEffect(() => { void load(1); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (c: PbxCall) => !c.endUtc && !c.disconnectUtc;
  const talk = (c: PbxCall): React.ReactNode => {
    if (c.duration != null && c.duration > 0) return fmtDuration(c.duration);
    if (c.acceptUtc && c.disconnectUtc) return fmtDuration(Math.max(0, Math.round((Date.parse(`${c.disconnectUtc}Z`) - Date.parse(`${c.acceptUtc}Z`)) / 1000)));
    return isActive(c) ? <OnCallChip count={1} size="sm" /> : "0:00";
  };

  return (
    <div className="pbx-tab-panel" style={{ display: "grid", gap: 12 }}>
      <RangeBar from={from} to={to} tz={tz} onChange={(f, t) => { setFrom(f); setTo(t); }} onGo={() => load(1)} busy={busy} />
      {!outboundTracking && (
        <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
          Calls dialled from this extension are not tracked yet (database script 16 and the PBX dialplan update); only calls handled by the extension are listed.
        </div>
      )}
      {err && <ErrorTile message={err} />}
      {data && data.items.length === 0 && <div style={{ color: "var(--portal-muted)" }}>No calls in this range.</div>}
      {data && data.items.length > 0 && (
        <ScrollShadow>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead><tr><Th>Start</Th><Th>Direction</Th><Th>ANI</Th><Th>Called number</Th><Th align="right">Ring</Th><Th align="right">Talk</Th><Th>Result</Th><Th>Rec.</Th></tr></thead>
            <tbody>
              {data.items.map((c, i) => (
                <tr key={`${c.statsAgentSessionId ?? "o"}-${c.callId ?? i}`}>
                  <Td>{fmtDateTime(c.startUtc, tz)}</Td>
                  <Td>{c.outbound ? <Pill>From extension</Pill> : <Pill tone="muted">To extension</Pill>}</Td>
                  <Td>
                    <span title={c.outbound ? "Number presented by the extension" : "Caller id presented to the extension"}>
                      {c.outbound ? (c.presentedNumber ? formatPhoneNumber(c.presentedNumber) : "hidden") : c.callingNumber ? formatPhoneNumber(c.callingNumber) : "—"}
                    </span>
                  </Td>
                  <Td>
                    <span title={c.outbound ? "Number the extension dialled" : c.dnisPresentation ? `Reached through ${formatPhoneNumber(c.dnisPresentation)}` : undefined}>
                      {c.outbound ? formatPhoneNumber(c.dialledNumber ?? c.calledNumber) : extensionLabel}
                    </span>
                  </Td>
                  <Td align="right">{c.ringSeconds != null ? `${c.ringSeconds} s` : "—"}</Td>
                  <Td align="right">{talk(c)}</Td>
                  <Td><span title={c.hangupCause ?? undefined}>{isActive(c) ? "IN PROGRESS" : (c.hangupCauseNormalised ?? "—")}</span></Td>
                  <Td>
                    {c.hasRecording && c.callId && (
                      <button type="button" className="btn btn-secondary" style={{ padding: "3px 7px" }} title="Play the recording" onClick={() => setPlaying(c)} aria-label="Play recording">
                        <FileAudio size={14} />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
      )}
      {data && <Pager page={data.page} pageSize={data.pageSize} total={data.total} onPage={(p) => load(p)} busy={busy} />}
      <RecordingModal agentId={agentId} call={playing} tz={tz} onClose={() => setPlaying(null)} />
    </div>
  );
}

/** Plays a call recording inline (fetched once as a blob, shared with the download button), like the recordings report. */
function RecordingModal({ agentId, call, tz, onClose }: { agentId: number; call: PbxCall | null; tz: string; onClose: () => void }) {
  const [audio, setAudio] = useState<{ callId: string; url: string } | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const callId = call?.callId ?? null;

  useEffect(() => {
    if (!callId) return;
    let active = true;
    setAudio(null);
    setAudioError(null);
    (async () => {
      try {
        const res = await fetch(pbxExtensionsApi.callAudioUrl(agentId, callId), { credentials: "include" });
        if (!res.ok) throw new Error(res.status === 404 ? "The recording file is not available (yet)." : `Could not load the recording (${res.status})`);
        const blob = await res.blob();
        if (!active) return;
        setAudio({ callId, url: URL.createObjectURL(blob) });
      } catch (e) {
        if (active) setAudioError(e instanceof Error ? e.message : "Could not load the recording");
      }
    })();
    return () => { active = false; };
  }, [agentId, callId]);

  useEffect(() => () => { if (audio) URL.revokeObjectURL(audio.url); }, [audio]);

  const download = () => {
    if (!audio) return;
    const a = document.createElement("a");
    a.href = audio.url;
    a.download = `REC_${audio.callId}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Modal
      open={call != null}
      onClose={onClose}
      title={call ? `Recording · ${fmtDateTime(call.startUtc, tz)}` : "Recording"}
      maxWidth={720}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={download} disabled={!audio} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Download size={16} /> Download
          </button>
          <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
        </>
      }
    >
      {call && (
        <div style={{ display: "grid", gap: 12 }}>
          {audio && audio.callId === callId ? (
            <WaveformPlayer key={audio.callId} src={audio.url} height={64} ariaLabel="Play recording" />
          ) : audioError ? (
            <ErrorTile message={audioError} />
          ) : (
            <div style={{ padding: 20, textAlign: "center", color: "var(--portal-muted)", fontSize: "0.9rem" }}>Loading recording…</div>
          )}
          <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
            {call.outbound ? `Extension → ${formatPhoneNumber(call.dialledNumber ?? call.calledNumber)}` : `${call.callingNumber ? formatPhoneNumber(call.callingNumber) : "—"} → extension`}
            {" · "}<span style={monoStyle}>{call.callId}</span>
          </div>
        </div>
      )}
    </Modal>
  );
}

